import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import PluginService from 'services/PluginService';
import { PLUGINS } from 'queries/query-keys';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import BigTable from '__components/Tables/Big';
import BigTableLayout, {
  useBigTableLayout,
} from '__components/Layout/BigTableLayout';
import PageToolbar, {
  PageSearchInput,
  PageToolbarSection,
  PageToolbarTitle,
} from '__components/Layout/PageToolbar';
import { TRow } from '__components/Kizen/Table';
import { useNavBarState } from 'app/navBarController';
import { APPS_TABS_HEIGHT } from 'pages/Apps/constants';
import Notice from '__components/Kizen/Notice';
import Loader from '__components/Kizen/Loader';
import { useBusinessAppPermissions } from 'pages/BusinessApps/permissions';
import { businessAppDetailsUrl } from 'pages/BusinessApps/constants';
import { ConfirmActionModal } from 'pages/BusinessApps/components/ConfirmActionModal';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import { useCurrentUserId } from 'hooks/useCurrentUserId';
import { useBusinessPluginApps } from 'ts-components/Plugins/useBusinessPluginApps';
import { PluginContext } from 'ts-components/Plugins/context';
import { useBusinessApps } from 'ts-components/Plugins/businessApps/useBusinessApps';
import {
  type PendingBusinessAppAction,
  useBusinessAppActions,
} from 'ts-components/Plugins/businessApps/useBusinessAppActions';
import { BUSINESS_APP_ACTIONS } from 'ts-components/Plugins/businessApps/constants';
import type { BusinessAppAction } from 'ts-components/Plugins/businessApps/helpers';
import useModal from '__components/Modals/useModal';
import { useMarketplacePermissions } from 'pages/Marketplace/permissions';
import { usePluginEnable } from 'pages/Marketplace/Details/usePluginEnable';
import { ConfirmationModal } from 'pages/Marketplace/components/Tile/ConfirmationModal';
import { PluginEnableModals } from 'pages/Marketplace/components/PluginEnableModals';
import type { MarketplacePlugin } from 'pages/Marketplace/types';
import { KizenError } from 'services/errors';
import { getOriginalErrorStatusCode } from 'services/AxiosService';
import {
  type InstalledAppRow,
  type InstalledFilters,
  type InstalledOrdering,
  buildInstalledRows,
  filterInstalledRows,
  hasBusinessRows,
  sortInstalledRows,
} from './rows';
import { useInstalledColumns, useTypeLabels } from './columns';
import { QuickFilters } from './QuickFilters';

const DEFAULT_PLUGIN_API_NAMES = ['google_ads', 'gmail_inbox', 'outlook_inbox'];

const StyledNotice = styled(Notice)`
  margin: 20px 20px 0;
`;

export const useInstalledRows = () => {
  const { t } = useTranslation();
  const permissions = useBusinessAppPermissions();
  const currentUserId = useCurrentUserId();
  const { canEditPlugins: canManageMarketplaceApps } =
    useMarketplacePermissions();
  const { plugins } = useContext(PluginContext);
  const businessPluginApps = useBusinessPluginApps(true);
  const businessApps = useBusinessApps('', {
    enabled: permissions.canViewBusinessApps,
  });
  const defaultPluginApps = useQuery<MarketplacePlugin[]>({
    queryKey: PLUGINS.GET_MOCKED_PLUGIN_APPS,
    queryFn: () =>
      Promise.all(
        DEFAULT_PLUGIN_API_NAMES.map((apiName) =>
          PluginService.getPluginApp(apiName)
        )
      ),
  });

  const rows = useMemo(
    () =>
      buildInstalledRows({
        t,
        businessPluginApps: businessPluginApps.data ?? [],
        businessApps: businessApps.data ?? [],
        plugins: plugins ?? {},
        permissions,
        currentUserId,
        canManageMarketplaceApps,
        defaultPluginApps: defaultPluginApps.data ?? [],
      }),
    [
      t,
      businessPluginApps.data,
      businessApps.data,
      plugins,
      permissions,
      currentUserId,
      canManageMarketplaceApps,
      defaultPluginApps.data,
    ]
  );

  return {
    rows,
    businessPluginApps: businessPluginApps.data,
    isPending:
      businessPluginApps.isPending ||
      (permissions.canViewBusinessApps && businessApps.isPending),
    isFetching: businessPluginApps.isFetching || businessApps.isFetching,
    error: businessPluginApps.error ?? businessApps.error,
  };
};

export const InstalledTab = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const typeLabels = useTypeLabels();
  const { rows, businessPluginApps, isPending, isFetching, error } =
    useInstalledRows();
  const { search, actualSearch, ordering, handleChangeSearch, headData } =
    usePaginationSettings({ ordering: 'name' });
  const [filters, setFilters] = useState<InstalledFilters>({
    type: null,
    attention: null,
  });
  const { height } = useNavBarState();
  const { filterDropdownRef, scrollTransitioned, ...bigTablePageProps } =
    useBigTableLayout(height + APPS_TABS_HEIGHT);

  const onActionCompleted = useCallback(
    ({ action, app }: PendingBusinessAppAction) => {
      if (action === BUSINESS_APP_ACTIONS.view) {
        history.push(businessAppDetailsUrl(app.api_name), {
          from: history.location.pathname,
        });
      } else if (action === BUSINESS_APP_ACTIONS.editConfiguration) {
        history.push(businessAppDetailsUrl(app.api_name, null, '/configure'), {
          from: history.location.pathname,
        });
      }
    },
    [history]
  );
  const { pending, isRunning, selectAction, confirmPending, cancelPending } =
    useBusinessAppActions({ onCompleted: onActionCompleted });

  const { refreshPluginArtifacts } = useContext(PluginContext);
  const businessPluginAppsByApiName = useMemo(
    () =>
      Object.fromEntries(
        (businessPluginApps ?? [])
          .filter((bpa) => bpa.source !== 'business')
          .map((bpa) => [bpa.plugin_app.api_name, bpa])
      ),
    [businessPluginApps]
  );
  const pluginEnable = usePluginEnable({
    businessPluginAppsByApiName,
    refetch: refreshPluginArtifacts,
  });
  const { onChangeEnabled } = pluginEnable;
  const [pluginToDisable, setPluginToDisable] =
    useState<MarketplacePlugin | null>(null);
  const [disableModalProps, , disableModal] = useModal({
    handleSubmit: () =>
      pluginToDisable &&
      onChangeEnabled(pluginToDisable.api_name, false, pluginToDisable),
  });

  const onSelectAction = useCallback(
    ({ value }: { value: BusinessAppAction }, row: InstalledAppRow) => {
      if (row.app) {
        selectAction({ action: value, app: row.app, version: row.liveVersion });
      } else if (
        row.marketplacePlugin &&
        value === BUSINESS_APP_ACTIONS.enable
      ) {
        onChangeEnabled(row.apiName, true, row.marketplacePlugin);
      } else if (
        row.marketplacePlugin &&
        value === BUSINESS_APP_ACTIONS.disable
      ) {
        setPluginToDisable(row.marketplacePlugin);
        disableModal.show();
      } else if (row.detailsUrl) {
        history.push(row.detailsUrl, { from: history.location.pathname });
      }
    },
    [selectAction, onChangeEnabled, disableModal, history]
  );

  const columns = useInstalledColumns({ onSelectAction });

  const visibleRows = useMemo(
    () =>
      sortInstalledRows(
        filterInstalledRows(rows, search, typeLabels, filters),
        (ordering || 'name') as InstalledOrdering
      ),
    [rows, search, typeLabels, filters, ordering]
  );

  if (error) {
    throw new KizenError(getOriginalErrorStatusCode(error) ?? 500);
  }

  if (isPending) {
    return <Loader loading />;
  }

  return (
    <>
      <BigTableLayout
        toolbar={
          <PageToolbar>
            <PageToolbarSection>
              <QuickFilters
                filters={filters}
                onChange={setFilters}
                showBusinessType={hasBusinessRows(rows)}
              />
            </PageToolbarSection>
            <PageToolbarTitle>{t('Installed Apps')}</PageToolbarTitle>
            <PageToolbarSection className="justify-end">
              <PageSearchInput
                placeholder={t('Search Apps')}
                value={actualSearch}
                onChange={handleChangeSearch}
                loading={isFetching}
                isClearable
                data-qa-id="installed-apps-search-input"
              />
            </PageToolbarSection>
          </PageToolbar>
        }
        recordTabSpacing={false}
        additionalNavHeight={APPS_TABS_HEIGHT}
        {...bigTablePageProps}
      >
        <BigTable
          tableTitle={
            !visibleRows.length && actualSearch ? (
              <StyledNotice>
                {t('No apps match "{{query}}".', { query: actualSearch })}
              </StyledNotice>
            ) : null
          }
          scrolledToTable={bigTablePageProps.scrolledToTable}
          columns={columns}
          head={<TRow head columns={columns} data={headData} />}
          staleData={isFetching || isRunning}
          data-qa-id="installed-apps-table"
        >
          {visibleRows.length ? null : <tr />}
          {visibleRows.map((row) => (
            <TRow
              key={row.id}
              columns={columns}
              data={row}
              data-qa-id={`installed-app-row-${row.apiName}`}
            />
          ))}
        </BigTable>
      </BigTableLayout>
      {pending ? (
        <ConfirmActionModal
          pending={pending}
          onConfirm={confirmPending}
          onHide={cancelPending}
        />
      ) : null}
      {pluginToDisable ? (
        <ConfirmationModal
          confirmationModalProps={disableModalProps}
          installedPlugin={pluginToDisable}
        />
      ) : null}
      <PluginEnableModals
        pluginEnable={pluginEnable}
        businessPluginAppsByApiName={businessPluginAppsByApiName}
      />
    </>
  );
};
